import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../../components/feature/AdminLayout';
import {
  fetchDemoTenantPerformance,
  type TrialPerformanceActivityItem,
  type TrialPerformanceReadModel,
} from '../../../api/demoTenants.api';

const ACTIVITY_TYPES = ['all', 'onboarding', 'customers', 'reservations', 'inventory', 'purchases', 'accounts', 'system'] as const;
const PERIODS = ['today', 'last_3_days', 'last_7_days', 'all'] as const;

function relativeTime(iso: string | null | undefined, locale: string): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const diffSec = Math.round((then - Date.now()) / 1000);
  const abs = Math.abs(diffSec);
  const rtf = new Intl.RelativeTimeFormat(locale.startsWith('ar') ? 'ar' : 'en', { numeric: 'auto' });
  if (abs < 60) return rtf.format(Math.round(diffSec), 'second');
  if (abs < 3600) return rtf.format(Math.round(diffSec / 60), 'minute');
  if (abs < 86400) return rtf.format(Math.round(diffSec / 3600), 'hour');
  return rtf.format(Math.round(diffSec / 86400), 'day');
}

function clock(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function statusDot(status: string): string {
  return (
    {
      active: 'bg-emerald-500',
      converted: 'bg-teal-600',
      expired: 'bg-gray-400',
      suspended: 'bg-amber-500',
    }[status] ?? 'bg-gray-400'
  );
}

function priorityTone(level: string): string {
  return (
    {
      high: 'bg-rose-50 text-rose-800 ring-rose-100',
      medium: 'bg-amber-50 text-amber-800 ring-amber-100',
      low: 'bg-gray-50 text-gray-600 ring-gray-100',
    }[level] ?? 'bg-gray-50 text-gray-600 ring-gray-100'
  );
}

function engagementTone(level: string): string {
  return (
    {
      hot: 'bg-rose-50 text-rose-800',
      warm: 'bg-amber-50 text-amber-800',
      cold: 'bg-slate-50 text-slate-600',
      inactive: 'bg-gray-100 text-gray-500',
    }[level] ?? 'bg-gray-50 text-gray-600'
  );
}

export default function TrialPerformancePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'ar';
  const tp = 'demo_tenants.performance';

  const [data, setData] = useState<TrialPerformanceReadModel | null>(null);
  const [items, setItems] = useState<TrialPerformanceActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [category, setCategory] = useState<(typeof ACTIVITY_TYPES)[number]>('all');
  const [period, setPeriod] = useState<(typeof PERIODS)[number]>('all');
  const [page, setPage] = useState(1);

  const load = useCallback(
    async (nextPage: number, append: boolean) => {
      if (!id) return;
      if (append) setLoadingMore(true);
      else setLoading(true);
      setError('');
      const result = await fetchDemoTenantPerformance(id, {
        category,
        period,
        page: nextPage,
        per_page: 30,
      });
      setLoading(false);
      setLoadingMore(false);
      if (result.ok === false) {
        if (result.unauthorized) {
          navigate('/admin/login', { replace: true });
          return;
        }
        setError(result.message);
        if (!append) {
          setData(null);
          setItems([]);
        }
        return;
      }
      setData(result.data);
      setItems((prev) => (append ? [...prev, ...result.data.activity.items] : result.data.activity.items));
      setPage(nextPage);
    },
    [id, category, period, navigate],
  );

  useEffect(() => {
    void load(1, false);
  }, [load]);

  const backPath = data?.trial.expired ? '/expired-demo-tenants' : '/demo-tenants';
  const meta = data?.activity.meta;
  const canLoadMore = Boolean(meta && page < meta.last_page);

  const metricCards = useMemo(() => {
    if (!data) return [];
    const m = data.business_metrics;
    return [
      { key: 'onboarding', value: `${data.onboarding.completed} / ${data.onboarding.total}` },
      { key: 'customers', value: String(m.customers ?? 0) },
      { key: 'reservations', value: String(m.reservations ?? 0) },
      { key: 'products', value: String(m.products ?? 0) },
      { key: 'purchase_orders', value: String(m.purchase_orders ?? 0) },
      { key: 'received_orders', value: String(m.received_orders ?? 0) },
      { key: 'inventory_items', value: String(m.inventory_items ?? 0) },
      { key: 'financial_activities', value: String(m.financial_activities ?? 0) },
    ];
  }, [data]);

  return (
    <AdminLayout>
      <div className="flex flex-col gap-4 max-w-[1400px]">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <button
            type="button"
            onClick={() => navigate(backPath)}
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-teal-700 cursor-pointer"
          >
            <i className="ri-arrow-right-line" />
            {t(`${tp}.back`)}
          </button>
        </div>

        {error ? (
          <div className="rounded-xl bg-rose-50 text-rose-800 text-sm px-4 py-3 ring-1 ring-rose-100">{error}</div>
        ) : null}

        {loading && !data ? (
          <div className="flex flex-col items-center justify-center py-24 gap-3 text-gray-500">
            <i className="ri-loader-4-line text-3xl text-teal-600 animate-spin" />
          </div>
        ) : data ? (
          <>
            <header className="bg-white rounded-xl ring-1 ring-gray-100 px-5 py-4">
              <p className="text-xs font-semibold text-gray-400">{t(`${tp}.title`)}</p>
              <div className="mt-1 flex flex-wrap items-center gap-3">
                <h1 className="text-xl font-bold text-gray-900">{data.trial.name}</h1>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-50 px-2.5 py-1 text-xs font-semibold text-gray-700">
                  <span className={`w-1.5 h-1.5 rounded-full ${statusDot(data.trial.status)}`} />
                  {t(`${tp}.status.${data.trial.status}`, { defaultValue: data.trial.status })}
                </span>
              </div>
              <dl className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div>
                  <dt className="text-xs text-gray-400">{t(`${tp}.started`)}</dt>
                  <dd className="font-medium text-gray-800">{relativeTime(data.trial.started_at, locale)}</dd>
                </div>
                <div>
                  <dt className="text-xs text-gray-400">{t(`${tp}.last_active`)}</dt>
                  <dd className="font-medium text-gray-800">{relativeTime(data.trial.last_active_at, locale)}</dd>
                </div>
                <div>
                  <dt className="text-xs text-gray-400">{t(`${tp}.duration`)}</dt>
                  <dd className="font-medium text-gray-800">
                    {t(`${tp}.duration_days`, { count: data.trial.duration_days })}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-gray-400">{t(`${tp}.onboarding`)}</dt>
                  <dd className="font-medium text-gray-800">{data.onboarding.percent}%</dd>
                </div>
              </dl>
            </header>

            <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                { label: t(`${tp}.health`), value: `${data.overview.health} / 100` },
                {
                  label: t(`${tp}.activation`),
                  value: t(`${tp}.activation_status.${data.overview.activation}`, {
                    defaultValue: data.overview.activation,
                  }),
                },
                { label: t(`${tp}.onboarding`), value: `${data.onboarding.percent}%` },
                { label: t(`${tp}.last_active`), value: relativeTime(data.overview.last_active_at, locale) },
              ].map((card) => (
                <div key={card.label} className="bg-white rounded-xl ring-1 ring-gray-100 px-4 py-3">
                  <p className="text-[11px] text-gray-400 font-semibold">{card.label}</p>
                  <p className="mt-1 text-lg font-bold text-gray-900">{card.value}</p>
                </div>
              ))}
            </section>

            <section className="grid grid-cols-1 xl:grid-cols-3 gap-4">
              <div className="xl:col-span-2 bg-white rounded-xl ring-1 ring-gray-100 p-5">
                <p className="text-xs font-semibold text-gray-400">{t(`${tp}.health`)}</p>
                <div className="mt-2 flex items-end justify-between gap-4">
                  <p className="text-4xl font-bold text-gray-900 tabular-nums">{data.health.score}</p>
                  <span className="text-sm font-semibold text-teal-700">
                    {t(`${tp}.health_band.${data.health.band}`, { defaultValue: data.health.band })}
                  </span>
                </div>
                <div className="mt-3 h-2 rounded-full bg-gray-100 overflow-hidden">
                  <div className="h-full bg-teal-600" style={{ width: `${Math.min(100, data.health.score)}%` }} />
                </div>
                <p className="mt-4 text-xs font-semibold text-gray-500">{t(`${tp}.why`)}</p>
                <ul className="mt-2 space-y-1">
                  {data.health.why.length === 0 ? (
                    <li className="text-sm text-gray-400">{t(`${tp}.none`)}</li>
                  ) : (
                    data.health.why.map((row) => (
                      <li key={row.key} className="flex items-center justify-between text-sm text-gray-700">
                        <span>{row.label}</span>
                        <span className="font-mono text-teal-700">+{row.points}</span>
                      </li>
                    ))
                  )}
                </ul>
              </div>

              <div className="flex flex-col gap-3">
                <div className={`rounded-xl ring-1 p-4 ${priorityTone(data.sales_priority.level)}`}>
                  <p className="text-xs font-semibold">{t(`${tp}.priority`)}</p>
                  <p className="mt-1 text-xl font-bold">
                    {t(`${tp}.priority_level.${data.sales_priority.level}`, {
                      defaultValue: data.sales_priority.level,
                    })}
                  </p>
                  <ul className="mt-2 space-y-0.5 text-xs">
                    {data.sales_priority.reasons.map((reason) => (
                      <li key={reason.key}>• {reason.label}</li>
                    ))}
                  </ul>
                </div>
                <div className="bg-white rounded-xl ring-1 ring-gray-100 p-4">
                  <p className="text-xs font-semibold text-gray-400">{t(`${tp}.engagement`)}</p>
                  <p className={`mt-2 inline-flex rounded-full px-2.5 py-1 text-sm font-bold ${engagementTone(data.engagement.level)}`}>
                    {t(`${tp}.engagement_level.${data.engagement.level}`, { defaultValue: data.engagement.level })}
                  </p>
                </div>
                <div className="bg-white rounded-xl ring-1 ring-gray-100 p-4">
                  <p className="text-xs font-semibold text-gray-400">{t(`${tp}.activation`)}</p>
                  <p className="mt-2 text-sm font-bold text-gray-900">
                    {t(`${tp}.activation_status.${data.activation.status}`, {
                      defaultValue: data.activation.status,
                    })}
                  </p>
                </div>
                <div className="bg-white rounded-xl ring-1 ring-gray-100 p-4">
                  <p className="text-xs font-semibold text-gray-400">{t(`${tp}.last_active`)}</p>
                  <p className="mt-1 text-lg font-bold text-gray-900">
                    {relativeTime(data.activity.last?.occurred_at ?? data.trial.last_active_at, locale)}
                  </p>
                  <p className="mt-1 text-sm text-gray-600">
                    {t(`${tp}.last_action`)}: {data.activity.last?.label ?? '—'}
                  </p>
                </div>
              </div>
            </section>

            <section className="bg-white rounded-xl ring-1 ring-gray-100 p-5">
              <p className="text-xs font-semibold text-gray-400">{t(`${tp}.summary`)}</p>
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 gap-3">
                {metricCards.map((card) => (
                  <div key={card.key} className="rounded-lg bg-gray-50 px-3 py-2">
                    <p className="text-[11px] text-gray-500">{t(`${tp}.metrics_keys.${card.key}`)}</p>
                    <p className="text-lg font-bold text-gray-900 tabular-nums">{card.value}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="grid grid-cols-1 xl:grid-cols-5 gap-4">
              <div className="xl:col-span-2 bg-white rounded-xl ring-1 ring-gray-100 p-5">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-gray-400">{t(`${tp}.journey`)}</p>
                  <p className="text-sm font-semibold text-gray-700">
                    {data.onboarding.completed} / {data.onboarding.total} — {data.onboarding.percent}%
                  </p>
                </div>
                <ol className="mt-4 space-y-2">
                  {data.onboarding.steps.map((step) => {
                    const done = step.status === 'completed';
                    return (
                      <li key={step.key} className="flex items-start gap-3">
                        <span
                          className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center text-[11px] ${
                            done ? 'bg-emerald-600 text-white' : 'bg-gray-200 text-gray-500'
                          }`}
                        >
                          {done ? '✓' : '○'}
                        </span>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900">{step.title}</p>
                          <p className="text-xs text-gray-500">
                            {done
                              ? `${step.last_activity_label}${step.completed_at ? ` · ${relativeTime(step.completed_at, locale)}` : ''}`
                              : step.last_activity_label}
                          </p>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              </div>

              <div className="xl:col-span-3 bg-white rounded-xl ring-1 ring-gray-100 p-5">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <p className="text-xs font-semibold text-gray-400">{t(`${tp}.activity`)}</p>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className="text-[11px] text-gray-400 self-center">{t(`${tp}.filters.type`)}</span>
                  {ACTIVITY_TYPES.map((value) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setCategory(value)}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium cursor-pointer ${
                        category === value ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {t(`${tp}.filters.${value}`)}
                    </button>
                  ))}
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  <span className="text-[11px] text-gray-400 self-center">{t(`${tp}.filters.period`)}</span>
                  {PERIODS.map((value) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setPeriod(value)}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium cursor-pointer ${
                        period === value ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {t(`${tp}.filters.${value === 'all' ? 'all_activity' : value}`)}
                    </button>
                  ))}
                </div>
                <ol className="mt-4 space-y-3">
                  {items.length === 0 ? (
                    <li className="text-sm text-gray-400 py-8 text-center">{t(`${tp}.empty_activity`)}</li>
                  ) : (
                    items.map((item) => (
                      <li key={item.id} className="flex items-start gap-3">
                        <span className="w-12 shrink-0 text-xs font-mono text-gray-400 pt-1">{clock(item.occurred_at)}</span>
                        <span className="w-8 h-8 rounded-lg bg-gray-50 text-teal-700 flex items-center justify-center">
                          <i className={`${item.icon || 'ri-flashlight-line'} text-base`} />
                        </span>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900">{item.label}</p>
                          <p className="text-xs text-gray-400">{relativeTime(item.occurred_at, locale)}</p>
                        </div>
                      </li>
                    ))
                  )}
                </ol>
                {canLoadMore ? (
                  <button
                    type="button"
                    disabled={loadingMore}
                    onClick={() => void load(page + 1, true)}
                    className="mt-4 w-full py-2 text-sm font-semibold text-teal-700 bg-teal-50 rounded-lg hover:bg-teal-100 disabled:opacity-40 cursor-pointer"
                  >
                    {loadingMore ? '…' : t(`${tp}.load_more`)}
                  </button>
                ) : null}
              </div>
            </section>

            <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl ring-1 ring-gray-100 p-5">
                <p className="text-xs font-semibold text-gray-400">{t(`${tp}.signals`)}</p>
                <ul className="mt-3 space-y-2">
                  {data.purchase_signals.map((signal) => (
                    <li key={signal.key} className="flex items-start gap-2 text-sm text-gray-800">
                      <span className={signal.done ? 'text-emerald-600' : 'text-gray-300'}>{signal.done ? '✓' : '○'}</span>
                      <span>{signal.label}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-4 rounded-lg bg-rose-50 px-3 py-2">
                  <p className="text-[11px] font-semibold text-rose-700">{t(`${tp}.recommendation`)}</p>
                  <p className="mt-1 text-sm font-medium text-rose-900">{data.recommendation.text}</p>
                </div>
              </div>

              <div className="bg-white rounded-xl ring-1 ring-gray-100 p-5">
                <p className="text-xs font-semibold text-gray-400">{t(`${tp}.pricing`)}</p>
                <ul className="mt-3 space-y-2">
                  {(
                    [
                      ['pricing_viewed', data.pricing.pricing_viewed],
                      ['upgrade_clicked', data.pricing.upgrade_clicked],
                      ['checkout_started', data.pricing.checkout_started],
                      ['payment', data.pricing.payment],
                    ] as const
                  ).map(([key, done]) => (
                    <li key={key} className="flex items-center justify-between text-sm">
                      <span className="text-gray-700">{t(`${tp}.pricing_keys.${key}`)}</span>
                      <span className={`font-bold ${done ? 'text-emerald-600' : 'text-gray-300'}`}>{done ? '✓' : '✗'}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-white rounded-xl ring-1 ring-gray-100 p-5">
                <p className="text-xs font-semibold text-gray-400">{t(`${tp}.lifecycle`)}</p>
                <ol className="mt-3 space-y-2">
                  {['created', 'started', 'activated', 'engaged', 'upgrade_intent', 'converted', 'expired'].map((stage) => {
                    const reached = data.lifecycle.stages.includes(stage);
                    const current = data.lifecycle.current === stage;
                    return (
                      <li key={stage} className="flex items-center gap-2">
                        <span
                          className={`w-2.5 h-2.5 rounded-full ${
                            current ? 'bg-teal-600' : reached ? 'bg-emerald-500' : 'bg-gray-200'
                          }`}
                        />
                        <span className={`text-sm ${current ? 'font-bold text-gray-900' : reached ? 'text-gray-700' : 'text-gray-400'}`}>
                          {t(`${tp}.lifecycle_stage.${stage}`)}
                        </span>
                      </li>
                    );
                  })}
                </ol>
              </div>
            </section>
          </>
        ) : null}
      </div>
    </AdminLayout>
  );
}
